import {type Line} from '../../../util/plotHelpers/types';
import {type NumericLine} from '../../vis/chartSeries';
type SinglePointLine =
  | Pick<Line, 'data' | 'aux'>
  | Pick<NumericLine, 'data' | 'role'>;
import {type SinglePointRenderMode} from '../../WorkspaceDrawer/Settings/types';
import {type RunsLinePlotConfig} from '../types';

export function isEmptyChart(
  config: Pick<RunsLinePlotConfig, 'metrics' | 'expressions'>
): boolean {
  const emptyMetrics = config.metrics == null || config.metrics.length === 0;
  const emptyExpressions =
    config.expressions == null || config.expressions.length === 0;
  return emptyMetrics && emptyExpressions;
}

export function isSinglePointChart(lines: SinglePointLine[]): boolean {
  const valid = lines.filter(isValidLine);
  return valid.length >= 1 && valid.every(l => l.data.length === 1);
}

export function hasMultipleSinglePointRuns(lines: SinglePointLine[]): boolean {
  const valid = lines.filter(isValidLine);
  return valid.length >= 2 && valid.every(l => l.data.length === 1);
}

// The effective render mode for a single-point chart. An explicit selection (or
// a new/reset workspace default) wins; otherwise we fall back to the original
// behavior — multiple single-point runs => bar, a single run => scatter.
export function resolveSinglePointRenderMode(
  lines: SinglePointLine[],
  selectedMode: SinglePointRenderMode | undefined
): SinglePointRenderMode {
  if (selectedMode != null) {
    return selectedMode;
  }
  return hasMultipleSinglePointRuns(lines) ? 'bar' : 'scatter';
}

export function isBarPlot(
  lines: SinglePointLine[],
  zooming: boolean = false,
  selectedMode: SinglePointRenderMode | undefined = undefined
): boolean {
  // this keeps a runs line plot from being converted to a bar plot when zooming
  // there is no way to zoom back out from a bar plot
  // TODO: change this behavior: https://weightsandbiases.slack.com/archives/CA4PBB2TF/p1708971196432519
  if (zooming) {
    return false;
  }

  // Only single-point charts (including a single run) are affected; anything
  // else renders as a normal line plot.
  if (!isSinglePointChart(lines)) {
    return false;
  }

  return resolveSinglePointRenderMode(lines, selectedMode) === 'bar';
}

export function isValidLine(l: SinglePointLine): boolean {
  return ('role' in l ? l.role === 'primary' : !l.aux) && l.data.length > 0;
}
